import { createContext, useContext } from 'react';

import {
  canEditProject,
  canEditTaskOwner,
  canManageTeam,
  canDeleteProject,
  canManageProjectSettings,
} from '../helpers/projectPermissions';
import type { ProjectAccess } from '../api/access';

interface ProjectAccessContextValue {
  access: ProjectAccess | null;
  loading: boolean;
  error: string | null;
  reloadAccess: () => void;
}

/**
 * Effective access for the project currently on screen.
 *
 * Exported so tests can supply a value directly; application code should use
 * the provider and the hooks below.
 */
export const ProjectAccessContext = createContext<ProjectAccessContextValue | null>(null);

/** Denied, unresolved access — what every consumer sees outside a provider. */
const DENIED: ProjectAccessContextValue = {
  access: null,
  loading: false,
  error: null,
  reloadAccess: () => {},
};

/**
 * Read the project access payload.
 *
 * Unlike `useModal`, this does not throw outside its provider — it returns
 * denied access, so a component rendered without one hides privileged actions
 * rather than crashing the page.
 */
export const useProjectAccessContext = (): ProjectAccessContextValue =>
  useContext(ProjectAccessContext) ?? DENIED;

/** Whether the user may edit project fields, including the scope of work. */
export const useCanEditProject = (): boolean =>
  canEditProject(useProjectAccessContext().access);

/** Whether the user may change a task's owner (Action Items + Project Plan). */
export const useCanEditTaskOwner = (): boolean =>
  canEditTaskOwner(useProjectAccessContext().access);

/** Whether the user may add, edit, remove or re-role team members. */
export const useCanManageTeam = (): boolean =>
  canManageTeam(useProjectAccessContext().access);

/**
 * Whether the user may delete the project.
 *
 * @param isOwner Whether the current user is the project's owner_id. Ownership
 *   lives on the project payload, not the access payload, so it stays a
 *   parameter — see `canDeleteProject`.
 */
export const useCanDeleteProject = (isOwner?: boolean): boolean =>
  canDeleteProject(useProjectAccessContext().access, isOwner);

/**
 * Whether the user may change project settings.
 *
 * @param isOwner Whether the current user is the project's owner_id.
 */
export const useCanManageProjectSettings = (isOwner?: boolean): boolean =>
  canManageProjectSettings(useProjectAccessContext().access, isOwner);
